import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { VideoPlayerErrorInfo, VideoPlayerRef } from '@/app/types';
import { cn } from '@/app/utils';
import type { BaseVideoPlayerProps } from '../../VideoPlayer/types';
import { shouldEmitNativeVideoError } from './feed-detail-media-frame-utils';

type NativeVideoError = VideoPlayerErrorInfo['error'];

export type FeedDetailNativeVideoPlayerProps = Pick<
  BaseVideoPlayerProps,
  'src' | 'poster' | 'autoPlay' | 'hasAudio' | 'defaultVolume' | 'onPlayEnd' | 'onError'
> & {
  className?: string;
};

export const FeedDetailNativeVideoPlayer = forwardRef<VideoPlayerRef, FeedDetailNativeVideoPlayerProps>(
  ({ className, src, poster, autoPlay, hasAudio = false, defaultVolume = 0.6, onPlayEnd, onError }, ref) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useImperativeHandle(ref, () => ({
      player: null,
      play: async () => {
        await videoRef.current?.play();
      },
      pause: () => {
        videoRef.current?.pause();
      },
      destroy: () => {
        videoRef.current?.pause();
        videoRef.current?.removeAttribute('src');
        videoRef.current?.load();
      },
      getVideoElement: () => videoRef.current,
    }));

    useEffect(() => {
      if (videoRef.current) {
        videoRef.current.volume = defaultVolume;
      }
    }, [defaultVolume]);

    const handleError = () => {
      const element = videoRef.current;
      const mediaError = element?.error;

      // 仅当浏览器真的设置了 MediaError 才上抛。spurious error 见
      // shouldEmitNativeVideoError 的注释。
      if (!shouldEmitNativeVideoError(mediaError)) return;

      onError?.({
        error: {
          playerVersion: 'native',
          src,
          currentTime: element?.currentTime ?? 0,
          duration: element?.duration ?? 0,
          ended: element?.ended ?? false,
          networkState: element?.networkState ?? 0,
          readyState: element?.readyState ?? 0,
          errorCode: mediaError.code,
          errorType: 'native_video_error',
          errorDetails: mediaError.message || 'native video error',
          message: mediaError.message || 'native video error',
          error: mediaError,
        } as unknown as NativeVideoError,
        src,
      });
    };

    return (
      <video
        ref={videoRef}
        className={cn('h-full w-full object-contain', className)}
        src={src}
        poster={poster}
        autoPlay={autoPlay}
        muted={!hasAudio}
        controls
        loop
        playsInline
        webkit-playsinline="true"
        x5-playsinline="true"
        preload="metadata"
        onEnded={onPlayEnd}
        onError={handleError}
      />
    );
  },
);

FeedDetailNativeVideoPlayer.displayName = 'FeedDetailNativeVideoPlayer';
